import React from 'react';

import {
	CheckCircleFilled,
	ExclamationCircleFilled,
	InfoCircleFilled,
	StopFilled,
} from '@ant-design/icons';
import { notification } from 'antd';
import { t } from 'i18next';
import styled from 'styled-components';

interface IconWrapperProps {
	$isOnlyTitle: boolean;
}

interface NotificationProps {
	description?: string | React.ReactNode;
	message?: string | React.ReactNode;
	duration?: number;
}

const IconWrapper = styled.div<IconWrapperProps>`
	font-size: ${(props) => (props.$isOnlyTitle ? '2rem' : '3rem')};
	line-height: 2rem;
`;

const EmptyDescription = styled.div`
	margin-top: -0.75rem;
`;

const openSuccessNotification = (config: NotificationProps): void => {
	notification.success({
		...config,
		icon: (
			<IconWrapper $isOnlyTitle={!config.description}>
				<CheckCircleFilled className='success-icon' />
			</IconWrapper>
		),
		message: <div className={`title ${!config.description && `title-only`}`}>{config.message}</div>,
		description: config.description ? (
			<div className='description ml-10'>{config.description}</div>
		) : (
			<EmptyDescription />
		),
		className: config.description ? '' : 'notification-without-description',
	});
};

const openInfoNotification = (config: NotificationProps): void => {
	notification.info({
		...config,
		icon: (
			<IconWrapper $isOnlyTitle={!config.description}>
				<InfoCircleFilled className='info-icon' />
			</IconWrapper>
		),
		message: <div className={`title ${!config.description && `title-only`}`}>{config.message}</div>,
		description: config.description ? (
			<div className='description ml-10'>{config.description}</div>
		) : (
			<EmptyDescription />
		),
		className: config.description ? '' : 'notification-without-description',
	});
};

const openWarningNotification = (config: NotificationProps): void => {
	notification.warning({
		...config,
		icon: (
			<IconWrapper $isOnlyTitle={!config.description}>
				<ExclamationCircleFilled className='warning-icon' />
			</IconWrapper>
		),
		message: <div className={`title ${!config.description && `title-only`}`}>{config.message}</div>,
		description: config.description ? (
			<div className='description ml-10'>{config.description}</div>
		) : (
			<EmptyDescription />
		),
		className: config.description ? '' : 'notification-without-description',
	});
};

const openErrorNotification = (config: NotificationProps): void => {
	if (!config.message) return;

	// Unhandled backend errors arrive as a raw JSON payload — never surface internals to the user.
	const message =
		typeof config.message === 'string' && config.message.trim().startsWith('{')
			? t('api.serverError')
			: config.message;

	notification.error({
		...config,
		icon: (
			<IconWrapper $isOnlyTitle={!config.description}>
				<StopFilled className='error-icon' />
			</IconWrapper>
		),
		message: <div className={`title ${!config.description && `title-only`}`}>{message}</div>,
		description: config.description ? (
			<div className='description ml-10'>{config.description}</div>
		) : (
			<EmptyDescription />
		),
		className: config.description ? '' : 'notification-without-description',
	});
};

export const notificationService = {
	success: openSuccessNotification,
	info: openInfoNotification,
	warning: openWarningNotification,
	error: openErrorNotification,
};
